import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, formatApiError, getStoredToken } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UploadSimple, Trash, Pause, Play } from "@phosphor-icons/react";

const GROUPS = [
  { k: "hold", label: "Hold / Queue / Voicemail" },
  { k: "time", label: "Time announcements" },
  { k: "number", label: "Numbers 0–60" },
  { k: "dtmf", label: "DTMF" },
];

const fmtSize = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDur = (s) => (s == null ? "—" : `${Number(s).toFixed(1)}s`);

const fmtWhen = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

function StatusPill({ status, enabled }) {
  if (status !== "uploaded") {
    return (
      <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground" data-testid="audio-slot-status">
        Missing
      </span>
    );
  }
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        enabled === false ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
      }`}
      data-testid="audio-slot-status"
    >
      {enabled === false ? "Disabled" : "Uploaded"}
    </span>
  );
}

function Preview({ asset }) {
  if (!asset?.id) return <span className="text-muted-foreground">—</span>;
  const token = getStoredToken();
  const src = `${API}/audio-library/${asset.id}/preview?auth=${encodeURIComponent(token || "")}&v=${encodeURIComponent(asset.updated_at || asset.id)}`;
  return (
    <audio
      key={src}
      controls
      preload="metadata"
      src={src}
      className="h-10 min-w-[14rem] w-64 max-w-full"
      data-testid={`audio-preview-${asset.id}`}
    >
      Your browser cannot play this recording.
    </audio>
  );
}

export default function AudioLibrary({ mode = "admin" }) {
  const isAdmin = mode === "admin";
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ can_upload: false, can_manage_global: false, allow_customer_audio_uploads: false });
  const [q, setQ] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const { data } = await api.get("/audio-library");
      setItems(data.items || []);
      setMeta({
        can_upload: !!data.can_upload,
        can_manage_global: !!data.can_manage_global,
        allow_customer_audio_uploads: data.allow_customer_audio_uploads,
        counts: data.counts,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => (
      `${it.label || ""} ${it.slot_key || ""} ${it.number_value ?? ""}`.toLowerCase().includes(needle)
    ));
  }, [items, q]);

  const byGroup = (g) => filtered.filter((it) => it.group === g);

  const pickFile = (slot, scope) => {
    uploadTarget.current = { slot, scope };
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = uploadTarget.current;
    if (!file || !target) return;
    const { slot, scope } = target;
    const key = `${scope}:${slot.slot_key}`;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = { category: slot.category, scope };
      if (slot.number_value != null) params.number_value = slot.number_value;
      await api.post("/audio-library/upload", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${slot.label} uploaded`);
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusyKey("");
    }
  };

  const toggle = async (asset) => {
    if (!asset?.id) return;
    setBusyKey(asset.id);
    try {
      await api.patch(`/audio-library/${asset.id}`, { enabled: !asset.enabled });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusyKey("");
    }
  };

  const remove = async () => {
    const asset = pendingDelete;
    setPendingDelete(null);
    if (!asset?.id) return;
    setBusyKey(asset.id);
    try {
      await api.delete(`/audio-library/${asset.id}`);
      toast.success("Recording deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setBusyKey("");
    }
  };

  const assetFor = (row, scope) => (scope === "global" ? row.global : row.tenant);

  const Actions = ({ row, scope }) => {
    const asset = assetFor(row, scope);
    const canManage = scope === "global" ? meta.can_manage_global : !!row.can_manage_tenant;
    if (!canManage) {
      return <span className="text-xs text-muted-foreground">{scope === "global" ? "Read only" : "—"}</span>;
    }
    const key = `${scope}:${row.slot_key}`;
    return (
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded-lg"
          disabled={!!busyKey}
          onClick={() => pickFile(row, scope)}
          data-testid={`audio-upload-${scope}-${row.slot_key}`}
        >
          <UploadSimple className="w-3.5 h-3.5 mr-1" />
          {asset?.status === "uploaded" ? "Replace" : "Upload"}
        </Button>
        {asset?.id && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-lg"
              disabled={busyKey === asset.id}
              onClick={() => toggle(asset)}
              data-testid={`audio-toggle-${asset.id}`}
            >
              {asset.enabled === false ? <Play className="w-3.5 h-3.5 mr-1" /> : <Pause className="w-3.5 h-3.5 mr-1" />}
              {asset.enabled === false ? "Enable" : "Disable"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-lg text-destructive"
              disabled={busyKey === asset.id}
              onClick={() => setPendingDelete(asset)}
              data-testid={`audio-delete-${asset.id}`}
            >
              <Trash className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </>
        )}
        {busyKey === key && <span className="text-xs text-muted-foreground self-center">Uploading…</span>}
      </div>
    );
  };

  const renderTable = (group) => {
    const rows = byGroup(group);
    return (
      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Format</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead>Preview</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-sm text-muted-foreground">No matching slots.</TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const showTenant = !isAdmin && row.tenant;
              const primary = isAdmin ? row.global : (row.tenant?.status === "uploaded" ? row.tenant : row.global);
              const scopeLabel = isAdmin ? "Global" : (primary?.scope === "tenant" ? "My recording" : "Global");
              return (
                <TableRow key={row.slot_key} data-testid={`audio-row-${row.slot_key}`}>
                  <TableCell className="font-medium">
                    {row.label}
                    <div className="text-[11px] text-muted-foreground">{row.slot_key}</div>
                  </TableCell>
                  <TableCell className="text-xs">{scopeLabel}</TableCell>
                  <TableCell><StatusPill status={primary?.status} enabled={primary?.enabled} /></TableCell>
                  <TableCell className="text-xs uppercase">{primary?.format || "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDur(primary?.duration_sec)}</TableCell>
                  <TableCell className="text-xs">{fmtSize(primary?.size)}</TableCell>
                  <TableCell className="text-xs">{fmtWhen(primary?.updated_at)}</TableCell>
                  <TableCell><Preview asset={primary} /></TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <Actions row={row} scope="global" />
                    ) : (
                      <div className="space-y-2">
                        {showTenant && <Actions row={row} scope="tenant" />}
                        {!showTenant && meta.can_upload && <Actions row={row} scope="tenant" />}
                        {!meta.can_upload && (
                          <p className="text-[11px] text-muted-foreground">Global default — upload disabled</p>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Customer Audios menu is only shown when uploads are enabled for the tenant.
  if (!isAdmin && !loading && meta.allow_customer_audio_uploads === false) {
    return <Navigate to="/call-limit" replace />;
  }

  return (
    <div className="space-y-5" data-testid="audio-library-page">
      <PageHeader
        title={isAdmin ? "Hold Music / Audios" : "Audios"}
        subtitle={
          isAdmin
            ? "Global recordings shared with every tenant. Customers can preview these but cannot change them."
            : "Preview platform recordings and upload your own private replacements."
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs"
          placeholder="Search name or number…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="audio-library-search"
        />
        <span className="text-xs text-muted-foreground">
          {meta.counts?.slots || 0} slots · {meta.counts?.numbers || 61} number recordings
        </span>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm"
        className="hidden"
        onChange={onFile}
        data-testid="audio-library-file"
      />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading audio library…</p>
      ) : (
        <Tabs defaultValue="hold">
          <TabsList>
            {GROUPS.map((g) => (
              <TabsTrigger key={g.k} value={g.k} data-testid={`audio-tab-${g.k}`}>{g.label}</TabsTrigger>
            ))}
          </TabsList>
          {GROUPS.map((g) => (
            <TabsContent key={g.k} value={g.k} className="mt-4">
              {g.k === "number" && (
                <p className="text-xs text-muted-foreground mb-3">
                  Every integer from 0 through 60 has its own slot. Missing recordings stay listed until uploaded.
                </p>
              )}
              {renderTable(g.k)}
            </TabsContent>
          ))}
        </Tabs>
      )}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recording?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the file from the library. Campaigns that selected it will fall back to the next available recording.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="audio-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
